import { useCallback, useState } from "react";
import { api } from "../lib/api-client.js";
import { type WorkflowState, fromDefinition, toYaml } from "../lib/yaml-serializer.js";

interface UseWorkflowResult {
	loading: boolean;
	error: string | null;
	load: (id: string) => Promise<WorkflowState | null>;
	deploy: (state: WorkflowState) => Promise<{ id: string; version: number } | null>;
	deleteWorkflow: (id: string) => Promise<boolean>;
	listWorkflows: () => Promise<Array<{ id: string; name: string; version: number }>>;
}

export function useWorkflow(): UseWorkflowResult {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async (id: string): Promise<WorkflowState | null> => {
		setLoading(true);
		setError(null);
		try {
			const workflow = await api.get<{ definition: unknown }>(`/workflows/${id}`);
			const state = fromDefinition(workflow.definition as Record<string, unknown>);
			state.id = id;
			setLoading(false);
			return state;
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to load workflow");
			setLoading(false);
			return null;
		}
	}, []);

	const deploy = useCallback(async (state: WorkflowState) => {
		setLoading(true);
		setError(null);
		try {
			const yaml = toYaml(state);
			const result = await api.post<{ id: string; version: number }>("/workflows", {
				definition: yaml,
			});
			setLoading(false);
			return result;
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to deploy workflow");
			setLoading(false);
			return null;
		}
	}, []);

	const deleteWorkflow = useCallback(async (id: string) => {
		try {
			await api.delete(`/workflows/${id}`);
			return true;
		} catch {
			return false;
		}
	}, []);

	const listWorkflows = useCallback(async () => {
		const result = await api.get<{
			workflows: Array<{ id: string; name: string; version: number }>;
		}>("/workflows");
		return result.workflows;
	}, []);

	return { loading, error, load, deploy, deleteWorkflow, listWorkflows };
}
